import { ErrorResponse, LatestReading, StreamEnd, TELEMETRY_END_EVENT, TELEMETRY_EVENT, } from "@spf/contracts";
import { createEventParser } from "./sse.js";
const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 30_000;
/** The server's code for "this account already holds its share of streams". */
const TOO_MANY = "too_many_streams";
/**
 * Holds a stream open, renewing the access token and reconnecting for as long
 * as the caller wants it.
 *
 * Reconnection is the whole point rather than a nicety: an access token is
 * short lived by design and the server ends the stream when it runs out, so a
 * dashboard that did not reconnect would go quiet after fifteen minutes and
 * look exactly like an inverter that had stopped reporting.
 */
export function streamTelemetry(deps, handlers) {
    const wait = deps.wait ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
    let controller = null;
    let stopped = false;
    let delay = RETRY_MIN_MS;
    const status = (next) => {
        if (!stopped)
            handlers.onStatus?.(next);
    };
    /**
     * A delivered reading is the only evidence that this connection is working,
     * and so the only thing that reports `live` or earns a prompt reconnect.
     *
     * Response headers are not that evidence. The server answers 200 and opens
     * the body before it reads the database, so a failing read produces an
     * accepted stream that carries nothing -- and treating the headers as proof
     * meant reporting a live connection that was not delivering, and resetting
     * the backoff into one reconnect per second from every open tab against a
     * database that was already struggling. Each attempt costs it an
     * authorization check and a read, so the retry meant to ride out a blip was
     * the thing prolonging it.
     */
    let delivered = false;
    const tracked = {
        ...handlers,
        onReading: (reading) => {
            delivered = true;
            delay = RETRY_MIN_MS;
            status("live");
            handlers.onReading(reading);
        },
    };
    async function loop() {
        while (!stopped) {
            status("connecting");
            controller = new AbortController();
            delivered = false;
            try {
                const outcome = await attempt(deps, tracked, controller);
                // There is no usable session left, and the transport has already
                // signed out. Retrying would only repeat that, forever.
                if (outcome === "signed-out")
                    return;
                const verdict = verdictOn(outcome, delivered);
                if (verdict !== null)
                    status(verdict);
            }
            catch (error) {
                if (stopped || isAbort(error))
                    return;
                status(error instanceof StreamUnavailable ? error.status : "offline");
            }
            if (stopped)
                return;
            // One wait for every way an attempt can end, because from here they are
            // the same thing: a connection that is not carrying data. A stream that
            // ran its full token lifetime still reconnects in RETRY_MIN_MS, because
            // the reading it delivered put the delay back to the floor.
            await wait(delay);
            delay = Math.min(delay * 2, RETRY_MAX_MS);
        }
    }
    void loop();
    return {
        close() {
            stopped = true;
            controller?.abort();
        },
    };
}
/**
 * What to tell the view about an attempt that has just finished, or null to
 * say nothing.
 *
 * A stream that opened and carried nothing is a failure whatever status line
 * it came with: the server opens the response before it reads the database, so
 * that is what an unreadable table looks like from here, and saying nothing
 * about it left the view on a loading spinner with no error and no retry --
 * which is not one of the states docs/REQUIREMENTS.md section 4 asks for.
 *
 * `renewed` is the exception. No reading is expected on an attempt that only
 * rotated a token, and the server ends every stream at token expiry, so
 * alarming there would put a false warning on a healthy dashboard once per
 * token lifetime.
 */
function verdictOn(outcome, delivered) {
    if (outcome === "unreachable")
        return "offline";
    return outcome === "ended" && !delivered ? "offline" : null;
}
/** One connection, from the request to the end of the body. */
async function attempt(deps, handlers, controller) {
    const token = deps.transport.tokens()?.accessToken ?? null;
    if (token === null)
        return "signed-out";
    const response = await deps.transport.fetch(`${deps.transport.baseUrl}${deps.path}`, {
        headers: { authorization: `Bearer ${token}`, accept: "text/event-stream" },
        signal: controller.signal,
        // No browser cache between a live stream and the server.
        cache: "no-store",
    });
    if (response.status === 401) {
        // The ordinary path: the server ends every stream when its access token
        // expires, and the reconnection carries that same expired token.
        const renewal = await deps.transport.renew();
        if (renewal.kind === "renewed")
            return "renewed";
        // A refresh the server refused is the end of the session, and the
        // transport has signed out. A refresh that never got an answer is a
        // network fault and nobody has been signed out -- treating that as a
        // sign-out ended live updates for the life of the tab, silently, because
        // one refresh request lost its answer.
        return renewal.kind === "rejected" ? "signed-out" : "unreachable";
    }
    if (!response.ok || response.body === null) {
        // Every refusal backs off; what differs is what the reader is told, so the
        // server's own error code is read rather than guessed from the status.
        throw new StreamUnavailable(await codeOf(response));
    }
    await consume(response.body, (event, data) => dispatch(handlers, event, data));
    return "ended";
}
function dispatch(handlers, event, data) {
    if (event === TELEMETRY_EVENT) {
        // Parsed against the contract like any other response. A stream is not a
        // reason to trust a body less carefully.
        const parsed = LatestReading.safeParse(JSON.parse(data));
        if (parsed.success)
            handlers.onReading(parsed.data);
        return;
    }
    if (event === TELEMETRY_END_EVENT) {
        const parsed = StreamEnd.safeParse(JSON.parse(data));
        if (parsed.success)
            handlers.onEnd?.(parsed.data.reason);
    }
}
class StreamUnavailable extends Error {
    status;
    constructor(status) {
        super(`The live stream was refused: ${status}.`);
        this.status = status;
        this.name = "StreamUnavailable";
    }
}
/**
 * Which of the two refusals this is. `too_many_streams` is the server saying
 * this account already holds its share of live views, which is a different
 * thing to say to a reader than "the server cannot be reached", and the only
 * one of the two they can do anything about.
 */
async function codeOf(response) {
    try {
        const body = JSON.parse(await response.text());
        const parsed = ErrorResponse.safeParse(body);
        if (parsed.success && parsed.data.error.code === TOO_MANY)
            return "crowded";
    }
    catch {
        // A refusal with no readable body says nothing beyond "not now".
    }
    return "offline";
}
/** Reads the body to its end, handing whole events to the caller. */
async function consume(body, receive) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    const parse = createEventParser((event) => {
        try {
            receive(event.event, event.data);
        }
        catch {
            // A single unreadable event is not a reason to drop the connection; the
            // next heartbeat carries the whole current state again.
        }
    });
    for (;;) {
        const { done, value } = await reader.read();
        if (done)
            break;
        parse(decoder.decode(value, { stream: true }));
    }
}
function isAbort(error) {
    return error instanceof Error && error.name === "AbortError";
}
//# sourceMappingURL=stream.js.map